// Where a publication lives and how the shell fetches its manifest. Kept free of three.js and
// zod so the first screen loads fast; full validation happens in the lazily loaded experience.

/** The CDN root: cdn.arcraftstudio.com in production, the dev server's /cdn proxy locally. */
export const CDN = (import.meta.env.VITE_CDN_URL ?? "/cdn").replace(/\/$/, "");

/** Short codes are what the QR code carries: /k7m2qxa (any case, optional trailing slash). */
export function codeFromPath(pathname: string): string | null {
  const match = /^\/([A-Za-z0-9]{4,16})\/?$/.exec(pathname);
  return match?.[1] ? match[1].toLowerCase() : null;
}

/** Collection pages: /c/k7m2qxa. */
export function collectionFromPath(pathname: string): string | null {
  const match = /^\/c\/([A-Za-z0-9]{4,16})\/?$/.exec(pathname);
  return match?.[1] ? match[1].toLowerCase() : null;
}

/** A file of the publication, from a manifest path like "v3/scene.json". */
export function publicationUrl(code: string, path: string) {
  return `${CDN}/p/${code}/${path}`;
}

export type ManifestFetch = { kind: "ok"; manifest: unknown } | { kind: "missing" } | { kind: "error" };

export function fetchManifest(code: string) {
  return fetchJson(publicationUrl(code, "manifest.json"));
}

export function fetchCollection(code: string) {
  return fetchJson(`${CDN}/c/${code}/manifest.json`);
}

async function fetchJson(url: string): Promise<ManifestFetch> {
  try {
    // Revalidate every time: publishing a new version changes these files.
    const res = await fetch(url, { cache: "no-cache" });
    // Public buckets answer 403 instead of 404 for files that don't exist (listing is private).
    if (res.status === 404 || res.status === 403) return { kind: "missing" };
    if (!res.ok) return { kind: "error" };
    return { kind: "ok", manifest: await res.json() };
  } catch {
    return { kind: "error" };
  }
}

/** The ARCraft app. Locally it runs next to the viewer (3100 for http, 3101 for https). */
function appUrl(location: Location) {
  const app =
    import.meta.env.VITE_APP_URL ??
    (import.meta.env.PROD
      ? "https://app.arcraftstudio.com"
      : `${location.protocol}//${location.hostname}:${location.protocol === "https:" ? 3101 : 3100}`);
  return app.replace(/\/$/, "");
}

/** The editor's draft preview, for team members who scan a card before it's published. */
export function draftPreviewUrl(code: string, location: Location) {
  return `${appUrl(location)}/preview/${code}`;
}

/**
 * Where a visitor reports an experience. It opens on the app, so the viewer itself still never
 * sends anything and collects nothing.
 */
export function reportUrl(code: string, location: Location, lang: string) {
  return `${appUrl(location)}/report/${encodeURIComponent(code)}?lang=${lang}`;
}
